import {
  CreateEventBlueprintBody,
  CreateEventBody,
  EditEventBody,
  EditEventSeriesBody,
  EventVisibility,
  getDateInTimezone,
  getDateTimeInTimezone,
  SeriesEditField,
  seriesEditChanges,
  SeriesEditValues,
} from '@seiri/shared/common'
import { CreateEventForm, CreateReoccuredEventForm } from './event-form.types'

/*
 * The event forms → the HTTP bodies (docs/validation.md, rule 4). The pickers yield browser-local
 * wall-clock `Date`s, which are reinterpreted in the **premise's** timezone and sent as UTC instants
 * (the Date/Time section of AGENTS.md). The bodies are unions on `allDay`, so the mapper decides which
 * times an event carries; a form missing them throws the same message the user would otherwise get
 * back from the API.
 */

const instant = (date: Date, timezone: string) => getDateTimeInTimezone(date, timezone).toISOString()

const eventCommon = (form: CreateEventForm) => ({
  name: form.name,
  activityId: form.activityId,
  placeId: form.placeId,
  capacity: form.capacity ?? 0,
  employeeIds: form.employeeIds,
  visibility: [form.visibility],
  active: !form.locked,
  cancellationDeadline: form.cancellationDeadline,
  allowCustomerNote: form.allowCustomerNote,
  // 0 (the form's default) is no price, so none is sent.
  ...(form.price ? { price: form.price } : {}),
  // '' is "Podľa činnosti": nothing is sent and the event shows its activity's colour.
  ...(form.color ? { color: form.color } : {}),
  // '' is no description; the API sanitises whatever is sent.
  ...(form.description ? { description: form.description } : {}),
})

export const toCreateEventBody = (form: CreateEventForm, timezone: string): CreateEventBody => {
  if (form.allDay) {
    if (!form.date) throw new Error('Musíš zadať dátum celodennej udalosti')
    return { ...eventCommon(form), allDay: true, date: instant(form.date, timezone) }
  }
  if (!form.start || !form.end) throw new Error('Musíš zadať začiatok a koniec udalosti')
  return { ...eventCommon(form), allDay: false, start: instant(form.start, timezone), end: instant(form.end, timezone) }
}

/**
 * The recurrence ends either on a date (the end of that day in the premise's timezone) or after a
 * number of occurrences - the form's `isReoccurNumOfTimes` switch picks which one is sent.
 */
export const toCreateEventBlueprintBody = (
  form: CreateReoccuredEventForm,
  timezone: string,
): CreateEventBlueprintBody => {
  if (form.isReoccurNumOfTimes && !form.reoccurNumOfTimes) throw new Error('Musíš zadať počet opakovaní')
  if (!form.isReoccurNumOfTimes && !form.reoccurUntil) {
    throw new Error('Musíš zadať dátum do kedy sa má udalosť opakovať')
  }
  const recurrence = {
    seriesName: form.seriesName,
    reoccurType: form.reoccurType,
    reoccurFrom: getDateInTimezone(form.reoccurFrom, timezone, 'start').toISOString(),
    ...(form.isReoccurNumOfTimes
      ? { reoccurNumOfTimes: form.reoccurNumOfTimes || 1 }
      : { reoccurUntil: getDateInTimezone(form.reoccurUntil as Date, timezone, 'end').toISOString() }),
    weekdays: form.weekdays,
    weeks: form.weeks,
    createEvents: true,
  }
  if (form.allDay) {
    return { ...eventCommon(form), ...recurrence, allDay: true }
  }
  if (!form.start || !form.end) throw new Error('Musíš zadať začiatok a koniec udalosti')
  return {
    ...eventCommon(form),
    ...recurrence,
    allDay: false,
    start: instant(form.start, timezone),
    end: instant(form.end, timezone),
  }
}

export interface EditEventFormValues {
  name: string
  activityId: string
  placeId: string
  capacity: number | null
  employeeIds: string[]
  start: Date | null
  end: Date | null
  date: Date | null
  allDay: boolean
  visibility: EventVisibility
  price: number | null
  /** A palette hex, or '' for "Podľa činnosti" - the activity's colour. */
  color: string
  /** HTML from the editor; '' removes the description. This event only, never its blueprint's others. */
  description: string
  /** For sign-ups from now on; each existing one keeps the deadline it was made with. */
  cancellationDeadline: number
  /** For sign-ups from now on; a note already written stays. */
  allowCustomerNote: boolean
  /** "Upozorniť účastníkov o zmene e-mailom": off unless the manager turns it on. Never compared as a change. */
  notifyCustomers: boolean
  /** Their message to the participants, sent only while `notifyCustomers` is on. */
  message: string
}

/** The notify fields of an edit body, present only when the manager asked; an empty message is left out. */
const notifyFields = (form: EditEventFormValues) =>
  form.notifyCustomers ? { notifyCustomers: true, ...(form.message.trim() && { message: form.message.trim() }) } : {}

/**
 * Everything the edit form holds is sent; an empty picker is left out, so the stored value stays.
 * A price of 0 is sent as `null`, which removes it, and so is an empty colour ("Podľa činnosti"),
 * which returns the event to its activity's colour. The description is always sent, `''` removing it.
 * With `timeLocked` (the event has participants, see `eventEditLock`) the time is left out too, so
 * the stored time stays whatever the pickers show. "Upozorniť účastníkov" travels only when it is on.
 */
export const toEditEventBody = (
  form: EditEventFormValues,
  timezone: string,
  { timeLocked = false }: { timeLocked?: boolean } = {},
): EditEventBody => ({
  name: form.name,
  activityId: form.activityId,
  placeId: form.placeId,
  ...(form.capacity !== null && { capacity: form.capacity }),
  employeeIds: form.employeeIds,
  visibility: [form.visibility],
  price: form.price || null,
  color: form.color || null,
  description: form.description,
  cancellationDeadline: form.cancellationDeadline,
  allowCustomerNote: form.allowCustomerNote,
  ...(!timeLocked && {
    allDay: form.allDay,
    ...(form.date && { date: instant(form.date, timezone) }),
    ...(form.start && { start: instant(form.start, timezone) }),
    ...(form.end && { end: instant(form.end, timezone) }),
  }),
  ...notifyFields(form),
})

/** The edit form's values in the fields a series edit compares (`seriesEditChanges`). */
export const toSeriesEditValues = (form: EditEventFormValues): SeriesEditValues => ({
  name: form.name,
  placeId: form.placeId || null,
  employeeIds: form.employeeIds,
  capacity: form.capacity ?? 0,
  price: form.price,
  color: form.color,
  visibility: [form.visibility],
  description: form.description,
  cancellationDeadline: form.cancellationDeadline,
  allowCustomerNote: form.allowCustomerNote,
})

/**
 * "Tento a nasledujúce": only the fields in play are sent - the ones `edited` changes against `opened`, and the ones in
 * `overwrite` ("Zjednotiť" on an unchanged field sends the opened termín's value) - so a field the manager did not
 * touch is never written to another termín. Values are sent as `toEditEventBody` sends them - no price and
 * "Podľa činnosti" as `null`.
 */
export const toEditEventSeriesBody = (
  opened: EditEventFormValues,
  edited: EditEventFormValues,
  overwrite: readonly SeriesEditField[] = [],
): EditEventSeriesBody => {
  const inPlay = new Set([...seriesEditChanges(toSeriesEditValues(opened), toSeriesEditValues(edited)), ...overwrite])
  return {
    changes: {
      ...(inPlay.has('name') && { name: edited.name }),
      ...(inPlay.has('placeId') && { placeId: edited.placeId }),
      ...(inPlay.has('employeeIds') && { employeeIds: edited.employeeIds }),
      ...(inPlay.has('capacity') && edited.capacity !== null && { capacity: edited.capacity }),
      ...(inPlay.has('price') && { price: edited.price || null }),
      ...(inPlay.has('color') && { color: edited.color || null }),
      ...(inPlay.has('visibility') && { visibility: [edited.visibility] }),
      ...(inPlay.has('description') && { description: edited.description }),
      ...(inPlay.has('cancellationDeadline') && { cancellationDeadline: edited.cancellationDeadline }),
      ...(inPlay.has('allowCustomerNote') && { allowCustomerNote: edited.allowCustomerNote }),
    },
    overwrite: [...new Set(overwrite)],
    ...notifyFields(edited),
  }
}
